import { Navigate, useLocation } from 'react-router-dom';
import { Spinner } from 'react-bootstrap';

// helpers
import { APICore } from '../helpers/api/apiCore';

// hooks
import { useUser, useSubscription } from '../hooks';
import SubscriptionWall from '../components/SubscriptionWall';

type PrivateRouteProps = {
    component: React.ComponentType;
    roles?: string;
    requireSubscription?: boolean;
};

/**
 * Private Route forces the authorization before the route can be accessed.
 * It can also enforce an active subscription for franchise-bound roles.
 */
const PrivateRoute = ({ component: RouteComponent, roles, requireSubscription = false }: PrivateRouteProps) => {
    const location = useLocation();
    const [loggedInUser] = useUser();
    const { loading: subscriptionLoading, isActive: hasActiveSubscription } = useSubscription();

    const api = new APICore();

    // Not logged in: redirect to login with return url
    if (api.isUserAuthenticated() === false) {
        return <Navigate to={'/auth/login'} state={{ from: location }} replace />;
    }

    // check if route is restricted by role
    if (roles && loggedInUser) {
        const rolesArray = roles.split(',').map((r) => r.trim());
        if (!rolesArray.includes(loggedInUser.role)) {
            // role not authorised so redirect to home page
            return <Navigate to={{ pathname: '/' }} />;
        }
    }

    // For franchise-bound roles, optionally enforce subscription
    const isFranchiseRole = loggedInUser && ['franchise_admin', 'staff'].includes(loggedInUser.role);
    if (requireSubscription && isFranchiseRole) {
        if (subscriptionLoading) {
            return (
                <div className="d-flex justify-content-center align-items-center py-5">
                    <Spinner animation="border" role="status" />
                </div>
            );
        }

        if (!hasActiveSubscription) {
            const canSubscribe = loggedInUser.role === 'franchise_admin';
            return (
                <SubscriptionWall
                    role={loggedInUser.role}
                    canSubscribe={canSubscribe}
                />
            );
        }
    }

    return <RouteComponent />;
};

export default PrivateRoute;
